import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import PageLoader from "@/components/base/Loading";
import AppHeader from "@/components/feature/AppHeader";
import AppFooter from "@/components/feature/AppFooter";
import { usePermissions } from "@/hooks/usePermissions";
import { ADMIN_MODULES, ROLE_LABELS, type AdminModule } from "@/lib/adminModules";

interface AdminRouteProps {
  children: ReactNode;
  module: AdminModule;
}

export default function AdminRoute({ children, module }: AdminRouteProps) {
  const { role, loading, hasAdminAccess, can } = usePermissions();
  const moduleMeta = ADMIN_MODULES.find((item) => item.key === module);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-50">
        <PageLoader label="正在校验管理员权限" />
      </div>
    );
  }

  if (!hasAdminAccess) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <AppHeader />
        <main className="flex flex-1 items-center justify-center px-4 py-20">
          <div className="w-full max-w-md rounded-lg border border-background-200 bg-background-100 p-8 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-md bg-accent-100 text-accent-700">
              <i className="ri-shield-user-line text-2xl" />
            </span>
            <h1 className="mt-4 font-heading text-xl font-semibold text-foreground-950">需要管理员权限</h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              当前账号暂无管理后台访问权限。管理员或客服账号可进入后台处理日常运营。
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/"
                className="w-full rounded-md bg-primary-500 px-5 py-2.5 text-center text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:w-auto"
              >
                返回首页
              </Link>
              <Link
                to="/settings"
                className="w-full rounded-md border border-background-300 px-5 py-2.5 text-center text-sm font-medium text-foreground-800 transition-colors hover:bg-background-50 sm:w-auto"
              >
                个人设置
              </Link>
            </div>
          </div>
        </main>
        <AppFooter />
      </div>
    );
  }

  if (!can(module)) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <AppHeader />
        <main className="flex flex-1 items-center justify-center px-4 py-20">
          <div className="w-full max-w-md rounded-lg border border-background-200 bg-background-100 p-8 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
              <i className="ri-lock-2-line text-2xl" />
            </span>
            <h1 className="mt-4 font-heading text-xl font-semibold text-foreground-950">无权访问该模块</h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              当前身份为「{ROLE_LABELS[role] ?? role}」，尚未被授予「{moduleMeta?.label ?? "该模块"}」的访问权限。
              如需开放，请联系超管在「管理员管理」中调整权限矩阵。
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/admin"
                className="w-full rounded-md bg-primary-500 px-5 py-2.5 text-center text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:w-auto"
              >
                返回管理后台
              </Link>
            </div>
          </div>
        </main>
        <AppFooter />
      </div>
    );
  }

  return <>{children}</>;
}